import { useCallback, useEffect } from 'react'
import { PopoverProps, PopoverSettings } from 'types/ui'

interface PopoverAnchorSettings {
  id: string
  show: PopoverProps['show']
  hide: PopoverProps['hide']
  visible: PopoverProps['visible']
  anchorElement: HTMLElement | null
  openOnFocus: PopoverSettings['openOnFocus']
  closeOnBlur: PopoverSettings['closeOnBlur']
  openOnAnchorHover: PopoverSettings['openOnAnchorHover']
  closeOnAnchorHoverExit: PopoverSettings['closeOnAnchorHoverExit']
  toggleOpenOnAnchorClick: PopoverSettings['toggleOpenOnAnchorClick']
}

export function usePopoverAnchor({
  id,
  show,
  hide,
  visible,
  anchorElement,
  openOnFocus = true,
  closeOnBlur = true,
  openOnAnchorHover,
  closeOnAnchorHoverExit,
  toggleOpenOnAnchorClick,
}: PopoverAnchorSettings) {
  // anchor element event handlers utilizing share/hide functions
  const handleFocus = useCallback(() => {
    if (openOnFocus && !visible) show()
  }, [openOnFocus, show, visible])

  const handleBlur = useCallback(() => {
    if (closeOnBlur && visible) hide()
  }, [closeOnBlur, hide, visible])

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) hide()
      if (e.key === 'Space') visible ? hide() : show()
    },
    [hide, show, visible]
  )

  const handleMouseEnter = useCallback(() => {
    if (openOnAnchorHover) show()
  }, [show, openOnAnchorHover])

  const handleMouseLeave = useCallback(() => {
    if (closeOnAnchorHoverExit) hide()
  }, [hide, closeOnAnchorHoverExit])

  const handleClick = useCallback(() => {
    if (toggleOpenOnAnchorClick) {
      !visible ? show() : hide()
    }
  }, [hide, show, toggleOpenOnAnchorClick, visible])

  // effect hook to set the attributes and event handlers on the anchor element
  useEffect(() => {
    // on anchor element change, set the id, aria-details, aria-describedby, mouseEnter, mouseLeave, click events
    if (anchorElement) {
      anchorElement.setAttribute('aria-details', id)
      anchorElement.setAttribute('aria-describedby', id)
      anchorElement.setAttribute('tabindex', '0')
      anchorElement.addEventListener('click', handleClick)
      anchorElement.addEventListener('focus', handleFocus)
      anchorElement.addEventListener('blur', handleBlur)
      anchorElement.addEventListener('keyup', handleKeyUp)
      anchorElement.addEventListener('mouseenter', handleMouseEnter)
      anchorElement.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      anchorElement?.removeEventListener('click', handleClick)
      anchorElement?.removeEventListener('focus', handleFocus)
      anchorElement?.removeEventListener('blur', handleBlur)
      anchorElement?.removeEventListener('keyup', handleKeyUp)
      anchorElement?.removeEventListener('mouseenter', handleMouseEnter)
      anchorElement?.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [
    anchorElement,
    handleBlur,
    handleClick,
    handleFocus,
    handleKeyUp,
    handleMouseEnter,
    handleMouseLeave,
    id,
  ])
}
